import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faPlus, faTimes, faSpinner } from '@fortawesome/free-solid-svg-icons';

// Colour picker for a listing.
//
// The same phone model is routinely stocked in several colours, and a seller
// shouldn't have to publish the listing three times to say so. This replaces
// the old single-value field: colours come from the model-specific lookup
// table (Admin → Categories → Device Colors) and are toggled on and off as
// chips, with a free-text fallback for models the team hasn't mapped yet.
//
// `value` is the array of selected colour names; the caller keeps the legacy
// single `color` string in sync from value[0] for older readers.
const ColorMultiSelect = ({
  value = [],
  onChange,
  availableColors = [],
  loading = false,
  modelEntered = false,
  isDarkMode = false,
}) => {
  const [customColor, setCustomColor] = useState('');

  const selected = Array.isArray(value) ? value : (value ? [value] : []);

  const toggle = (color) => {
    const exists = selected.some(c => c.toLowerCase() === color.toLowerCase());
    onChange(exists
      ? selected.filter(c => c.toLowerCase() !== color.toLowerCase())
      : [...selected, color]);
  };

  const addCustom = () => {
    const c = customColor.trim();
    if (!c) return;
    if (!selected.some(s => s.toLowerCase() === c.toLowerCase())) {
      onChange([...selected, c]);
    }
    setCustomColor('');
  };

  const muted = isDarkMode ? '#94a3b8' : '#6b7280';
  const border = isDarkMode ? 'rgba(255,255,255,0.14)' : '#cbd5e1';

  // Colours already chosen that aren't in the preset list (typed in by hand,
  // or from an older listing whose model has since been remapped) still need
  // somewhere to render, or editing a listing would silently drop them.
  const extras = selected.filter(
    s => !availableColors.some(a => a.toLowerCase() === s.toLowerCase())
  );
  const chips = [...availableColors, ...extras];

  return (
    <div>
      {loading && (
        <div style={{ fontSize: '0.85rem', color: muted, padding: '0.4rem 0' }}>
          <FontAwesomeIcon icon={faSpinner} spin /> Loading colours for this model…
        </div>
      )}

      {!loading && chips.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.45rem', marginBottom: '0.6rem' }}>
          {chips.map(color => {
            const isOn = selected.some(c => c.toLowerCase() === color.toLowerCase());
            return (
              <button
                key={color}
                type="button"
                onClick={() => toggle(color)}
                aria-pressed={isOn}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
                  padding: '0.4rem 0.75rem', borderRadius: '99px', cursor: 'pointer',
                  fontSize: '0.82rem', fontWeight: isOn ? 700 : 500,
                  border: `1px solid ${isOn ? '#22C55E' : border}`,
                  background: isOn
                    ? (isDarkMode ? 'rgba(34,197,94,0.18)' : 'rgba(34,197,94,0.1)')
                    : 'transparent',
                  color: isOn ? (isDarkMode ? '#4ade80' : '#16A34A') : 'inherit',
                  transition: 'all 0.15s ease',
                }}
              >
                {isOn && <FontAwesomeIcon icon={faCheck} style={{ fontSize: '0.7rem' }} />}
                {color}
              </button>
            );
          })}
        </div>
      )}

      {/* Free-text entry — the only route for an unmapped model, and a useful
          escape hatch for a colourway missing from the preset list. */}
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <input
          type="text"
          value={customColor}
          onChange={(e) => setCustomColor(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustom(); } }}
          placeholder={
            !modelEntered ? 'Enter the model first'
              : chips.length > 0 ? 'Add another colour…'
              : 'e.g., Space Black'
          }
          disabled={loading}
          style={{ flex: 1 }}
        />
        <button
          type="button"
          onClick={addCustom}
          disabled={!customColor.trim()}
          style={{
            padding: '0 0.9rem', borderRadius: '8px', cursor: customColor.trim() ? 'pointer' : 'not-allowed',
            border: `1px solid ${border}`, background: 'transparent', color: 'inherit',
            opacity: customColor.trim() ? 1 : 0.45, fontWeight: 600, whiteSpace: 'nowrap',
          }}
        >
          <FontAwesomeIcon icon={faPlus} /> Add
        </button>
      </div>

      {selected.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginTop: '0.7rem', alignItems: 'center' }}>
          <span style={{ fontSize: '0.8rem', color: muted, fontWeight: 600 }}>
            Listing in {selected.length} colour{selected.length > 1 ? 's' : ''}:
          </span>
          {selected.map(color => (
            <span
              key={color}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
                padding: '0.25rem 0.5rem 0.25rem 0.65rem', borderRadius: '99px',
                fontSize: '0.78rem', fontWeight: 600,
                background: isDarkMode ? 'rgba(34,197,94,0.16)' : 'rgba(34,197,94,0.1)',
                color: isDarkMode ? '#4ade80' : '#16A34A',
              }}
            >
              {color}
              <button
                type="button"
                onClick={() => toggle(color)}
                aria-label={`Remove ${color}`}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', padding: '0 0.1rem', fontSize: '0.7rem', lineHeight: 1 }}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </span>
          ))}
        </div>
      )}

      {!loading && modelEntered && availableColors.length === 0 && (
        <small style={{ display: 'block', color: muted, fontSize: '0.85rem', marginTop: '0.5rem' }}>
          No preset colour list for this model yet — type each colour and press Add.
        </small>
      )}
    </div>
  );
};

export default ColorMultiSelect;
